import { Listbox } from '@headlessui/react';
import { ArrowDown, Check, ChevronDown, ChevronUp } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip';
import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import { getAllowedRelationships } from '@/sections/insights/queryBuilder/entities/relationships';
import type {
  FlatGraphEdge,
  FlatGraphNode,
} from '@/sections/insights/queryBuilder/types/FlatGraph';

interface EdgeLabelProps {
  edge: FlatGraphEdge;
  sourceNode: FlatGraphNode;
  targetNode: FlatGraphNode;
  isExpanded: boolean;
  editable: boolean;
  onToggle: (edgeId: string) => void;
  onUpdateLabel?: (edgeId: string, newLabels: string[]) => void;
}

/**
 * EdgeLabel - Displays edge icon, label (editable select or display), and direction
 */
export const EdgeLabel = ({
  edge,
  sourceNode,
  targetNode,
  isExpanded,
  editable,
  onToggle,
  onUpdateLabel,
}: EdgeLabelProps) => {
  // Get allowed relationships based on source/target node labels
  const allowedRelationships = getAllowedRelationships(
    sourceNode.data.labels?.[0] ?? '',
    targetNode.data.labels?.[0] ?? ''
  );

  // Check if relationships are available
  const isDisabled = allowedRelationships.length === 0;

  // Get display names for source and target nodes
  const sourceLabel = sourceNode.data.labels?.[0] ?? '';
  const targetLabel = targetNode.data.labels?.[0] ?? '';

  const sourceEntity = getQueryEntity(sourceLabel);
  const targetEntity = getQueryEntity(targetLabel);

  const sourceDisplayName =
    (sourceEntity?.primaryLabel.display ??
      sourceEntity?.primaryLabel.actual ??
      sourceLabel) ||
    'source';
  const targetDisplayName =
    (targetEntity?.primaryLabel.display ??
      targetEntity?.primaryLabel.actual ??
      targetLabel) ||
    'target';

  // Handle both string and array labels
  const labels = Array.isArray(edge.data.label)
    ? edge.data.label
    : [edge.data.label];

  // Create lookup map: actual → display
  const labelMap = new Map(
    allowedRelationships.map(rel => [rel.actual, rel.display])
  );

  // Map raw labels to display labels
  const displayLabels = labels.map(label => labelMap.get(label) ?? label);

  // Condensed display format
  const displayText = isDisabled
    ? 'No relationships available'
    : displayLabels.length === 0
      ? 'Select relationship...'
      : displayLabels.length > 2
        ? `${displayLabels[0]}, +${displayLabels.length - 1}`
        : displayLabels.join(', ');

  const handleLabelsChange = (newLabels: string[]) => {
    onUpdateLabel?.(edge.id, newLabels);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onToggle(edge.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(edge.id);
        }
      }}
      className="hover:bg-chariot-background-secondary flex w-full cursor-pointer items-center gap-3 p-3 text-left"
      data-testid={`edge-card-${edge.id}`}
      aria-expanded={isExpanded}
      aria-controls={`edge-content-${edge.id}`}
      aria-label={`${displayText} relationship`}
    >
      <ArrowDown className="text-chariot-text-primary h-6 w-6" />
      <div className="flex-1">
        {editable ? (
          <Listbox
            value={labels}
            onChange={handleLabelsChange}
            multiple
            disabled={isDisabled}
          >
            <div className="relative">
              {isDisabled ? (
                <Tooltip
                  title={`No relationship types exist between ${sourceDisplayName} and ${targetDisplayName}. Please select different node labels.`}
                >
                  <Listbox.Button
                    onClick={e => e.stopPropagation()}
                    className="text-chariot-text-primary focus:ring-chariot-brand-primary cursor-not-allowed rounded border-none bg-transparent font-medium opacity-70 focus:ring-2 focus:outline-none"
                    aria-label="Change edge types"
                    data-testid={`edge-label-listbox-${edge.id}`}
                  >
                    {displayText}
                  </Listbox.Button>
                </Tooltip>
              ) : (
                <Listbox.Button
                  onClick={e => e.stopPropagation()}
                  className="text-chariot-text-primary focus:ring-chariot-brand-primary cursor-pointer rounded border-none bg-transparent font-medium hover:underline focus:ring-2 focus:outline-none"
                  aria-label="Change edge types"
                  data-testid={`edge-label-listbox-${edge.id}`}
                >
                  {displayText}
                </Listbox.Button>
              )}

              <Listbox.Options className="bg-chariot-background-primary border-chariot-stroke absolute left-0 z-10 mt-1 max-h-60 w-64 overflow-y-auto rounded-md border shadow-lg">
                {allowedRelationships.map(rel => (
                  <Listbox.Option
                    key={rel.actual}
                    value={rel.actual}
                    className={({ active }) =>
                      `flex cursor-pointer items-center gap-2 px-3 py-2 ${
                        active ? 'bg-chariot-background-secondary' : ''
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <Check
                          className={`h-4 w-4 ${selected ? 'text-chariot-brand-primary visible' : 'invisible'}`}
                        />
                        <span className={selected ? 'font-semibold' : ''}>
                          {rel.display}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
        ) : (
          <div className="text-chariot-text-primary font-medium">
            {displayText}
          </div>
        )}
      </div>
      {isExpanded ? (
        <ChevronUp className="text-chariot-text-primary" />
      ) : (
        <ChevronDown className="text-chariot-text-primary" />
      )}
    </div>
  );
};
